/**
 * Channel detail → "Today's Performance" grid mapping (pure).
 *
 * Each channel page reads its own slice of `daily_affiliate_activity`, filtered to the
 * channel's affiliate type (`aft`), the same per-`aft` source the Home Acquisition
 * Channels cards use. The grid is a Today snapshot with "vs yesterday" deltas (no range
 * selector). Kept free of Redux/effects so it can be unit tested; the fetching lives in
 * {@link useChannelPerformance}.
 *
 * Like the Home channel cards, these read live but show ZEROS until the backend tags
 * affiliate rows with a non-empty `aft` (verified gap, see `docs/api.owners-hub.stats.md`).
 */
import type { AffiliateType } from '../home/stats-mapping';
import type { Bucket } from '../home/stats-shape';
import type { ChannelId, ChannelMetric, KpiStat } from '../../mock-data/types';
import { type AcqKpiSpec, buildAdSpendStat, buildStat } from '../acquisition/performance-stats';

/** Channel detail page id → the `aft` literal its figures are filtered to. */
export const CHANNEL_AFT: Record<ChannelId, AffiliateType> = {
  'web-networks': 'web',
  'app-networks': 'app',
  'crypto-sites': 'crypto',
  'short-videos': 'video',
};

/**
 * How each Stats-sourced "Today's Performance" metric is read from
 * `daily_affiliate_activity` (filtered to the channel's `aft`), keyed by metric `id`.
 * Daily Spend is NOT here — it has no stats field; it is sourced from the channel's
 * Casino Ad Service ad-spend `cost` instead (see {@link buildChannelMetrics}).
 */
export const CHANNEL_METRIC_SPECS: Record<string, AcqKpiSpec> = {
  registrations: { kind: 'sum', fields: ['registrations'], money: false },
  ftds: { kind: 'sum', fields: ['ftds'], money: false },
  'avg-ftd-value': { kind: 'ratio', num: ['ftd_total_amount'], den: ['ftds'], money: true },
};

/**
 * The channel's ad-spend inputs for the cost-backed figures (the Daily Spend tile + the
 * CPA card): today's cost and the yesterday baseline, plus the channel's today / yesterday
 * `aft` buckets that CPA divides by for FTDs. `cost` is `null` until the spend resolves;
 * `errored` flips the figures to `N/A`; `loading` renders a blank (skeleton) instead of a
 * premature `?`. Mirrors the acquisition root's {@link buildAdSpendStat} column.
 */
export type ChannelAdSpend = {
  cost: number | null;
  baselineCost: number | null;
  current: Bucket[];
  baseline: Bucket[];
  errored: boolean;
  loading: boolean;
};

/** Blank stat (no value, no marker) — while loading, so the page skeletons the figure
 *  rather than flashing a `?` the moment the cost lands resolves it. */
const BLANK_STAT: KpiStat = { value: '', change: '', changeType: 'increase' };

/** The ad-spend column the cost-backed builders consume (drops `loading`). */
const adColumn = (ad: ChannelAdSpend) => ({
  cost: ad.cost,
  baselineCost: ad.baselineCost,
  current: ad.current,
  baseline: ad.baseline,
  errored: ad.errored,
});

/**
 * Override each metric's figure (value + delta) with the API-sourced one, leaving the
 * static `id` / `label` untouched. Registrations / FTDs / Avg FTD Value come from the
 * Stats buckets; Daily Spend comes from the channel ad-spend `cost` (blank while it
 * loads, `N/A` on failure). Pure (no hooks) so it unit-tests directly.
 */
export const buildChannelMetrics = (
  scaffold: ChannelMetric[],
  current: Bucket[],
  baseline: Bucket[],
  errored: boolean,
  ad: ChannelAdSpend,
): ChannelMetric[] =>
  scaffold.map((metric) => {
    if (metric.id === 'daily-spend') {
      return { ...metric, ...(ad.loading ? BLANK_STAT : buildAdSpendStat('budget-spent', adColumn(ad))) };
    }
    const spec = CHANNEL_METRIC_SPECS[metric.id];
    return spec ? { ...metric, ...buildStat(spec, current, baseline, errored) } : metric;
  });

/**
 * Build the top "Cost per Acquisition" card from the channel ad-spend ÷ FTDs (today, vs
 * yesterday), reusing the acquisition root's blended-CPA builder: `cost / FTDs`, `–` when
 * zero FTDs, `N/A` on failure, blank (skeleton) while the cost loads. Pure.
 */
export const buildChannelCpa = (scaffold: ChannelMetric, ad: ChannelAdSpend): ChannelMetric => ({
  ...scaffold,
  ...(ad.loading ? BLANK_STAT : buildAdSpendStat('blended-cpa', adColumn(ad))),
});
